import React, { useEffect, useRef, useState } from 'react'
import { Box, DialogContent, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import { styled } from '@mui/material/styles'
import LogoHandover from './LogoHandover'
import StageCopy from './StageCopy'
import ProgressDots from './ProcessDots'
import TrustPointsCard from './TrustPointsCard'
import LoadingSpinner from './LoadingSpinner'
import type { InterstitialStage } from '../../../../../types/ui'

type FormlessJourneyV3InterstitialContentProps = {
  onComplete?: () => void
  stageOverride?: InterstitialStage
  autoAdvance?: boolean
  isPreapproved?: boolean
  source: string
}

const MotionBox = motion(Box)

const RootCard = styled(Box)(() => ({
  maxWidth: '500px',
  width: '100%',
  margin: '0 auto',
  textAlign: 'center'
}))

// Durations (ms)
const INTRO_DURATION = 1200 // how long intro text stays before transition
const TRANSITION_DURATION = 2200 // how long transition text stays before complete
const FADE_OUT_OFFSET = 2500 // when to start fade-out after complete
const COMPLETE_OFFSET = 3500 // when to call onComplete after complete



const FormlessJourneyAnimatedInterstitialContent = ({ onComplete, stageOverride, autoAdvance = true, isPreapproved, source }: FormlessJourneyV3InterstitialContentProps): JSX.Element => {
  const [stage, setStage] = useState<InterstitialStage>(stageOverride ?? 'intro')
  const [fadeOut, setFadeOut] = useState(false)

  const onCompleteRef = useRef<FormlessJourneyV3InterstitialContentProps['onComplete']>()
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    if (stageOverride) {
      setStage(stageOverride)
      setFadeOut(false)
      return
    }

    setStage('intro')
    setFadeOut(false)

    if (!autoAdvance) return

    const tIntroToTransition = setTimeout(() => {
      setStage('transition')
    }, INTRO_DURATION)

    const tTransitionToComplete = setTimeout(() => {
      setStage('complete')
    }, INTRO_DURATION + TRANSITION_DURATION)

    const tStartFadeOut = setTimeout(() => {
      setFadeOut(true)
    }, INTRO_DURATION + TRANSITION_DURATION + FADE_OUT_OFFSET)

    const tCallOnComplete = setTimeout(() => {
      if (onCompleteRef.current) {
        onCompleteRef.current()
      }
    }, INTRO_DURATION + TRANSITION_DURATION + COMPLETE_OFFSET)

    return () => {
      clearTimeout(tIntroToTransition)
      clearTimeout(tTransitionToComplete)
      clearTimeout(tStartFadeOut)
      clearTimeout(tCallOnComplete)
    }
  }, [stageOverride, autoAdvance])

  return (
    <DialogContent sx={{ p: 0, maxWidth: 500 }}>
      <MotionBox
        animate={{ opacity: fadeOut ? 0 : 1 }}
        transition={{ duration: 0.8 }}
        sx={{
          position: 'relative',
          overflow: 'hidden'
        }}
        bgcolor={'common.white'}
      >
        <RootCard>
          <Box sx={{ marginTop: '16px' }}>
            <LogoHandover stage={stage} source={source} />

            <MotionBox
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              sx={{ marginBottom: '32px' }}
            >
              <StageCopy
                stage={stage}
                isPreapproved={isPreapproved}
                source={source}
              />
            </MotionBox>

            <ProgressDots stage={stage} />
            <TrustPointsCard
              stage={stage}
              isPreapproved={isPreapproved}
            />
            <LoadingSpinner stage={stage} />
          </Box>
        </RootCard>

        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 1.5 }}
          sx={{
            marginTop: '80px',
            textAlign: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block'
            }}
          >
            {source === 'clearscore' ? 'Powered by ClearScore&apos;s exclusive partnership with Zuto' : 'Zuto is a credit broker, not a lender.'}
          </Typography>
        </MotionBox>
      </MotionBox>
    </DialogContent>
  )
}

export default FormlessJourneyAnimatedInterstitialContent
